import { courseCopyKey } from "./copy-key.js";
export const COURSE_COPY_PREFIX = "@course-copy:";
const copyFields = new Set(["en", "qEn", "titleEn", "subtitle", "note", "direction", "canDo", "situation", "goal", "description", "role", "prompt", "why", "about", "heading", "p", "tip", "caption", "meaning", "definition", "summary", "hint", "label", "term", "turn", "specialInfo", "carrierNote", "varietyNote"]);
const scopeTokens = new Set(["scene", "extension", "module", "course"]);
const nativeTitles = new Set(["words", "grammar", "story", "course-words", "course-grammar", "course-story", "course-scene"]);
const phoneticFields = new Set(["sound", "breakdown"]);
const scriptCopyFields = new Set(["promise", "alt", "exampleTitle", "characterTitle", "characterGoal", "compositionGoal", "dialogTitle", "carrier", "audioLabel", "cueLabel", "symbolTitle", "year"]);
const englishPhoneticWords = new Set("sign often nasal dot moon vowel no inherent changes a consonant initial exception sound rolled final links before spelled sounds link in speech silent initially finally lax airy tense carrier short open syllable long is pronounced this common word low class live mid dead dependent tone high rising falling closes an pattern sometimes repeat the previous or phrase abbreviation currency symbol obsolete rare shortener cancellation mark instruction".split(" "));
const phoneticDescription = /\b(?:sign|often|nasal|inherent|changes|initial|exception|sound|rolled|final|links|spelled|sounds|silent|initially|finally|lax|airy|tense|carrier|short|long|syllable|pronounced|class|live|dead|tone|rising|falling|closes|sometimes|repeat|abbreviation|currency|obsolete|rare|shortener|cancellation|dependent)\b/i;
const nativeSamples = text => [...String(text || "").matchAll(/(?<!\*)\*([^*]+)\*(?!\*)/g)].map(match => match[1]);
const placeholders = text => [...String(text || "").matchAll(/\{[^{}]+\}/g)].map(match => match[0]).sort();
export function courseCopySourceText(text, domain = "", context = {}, overrides = {}) {
  const map = overrides.overrides || overrides.strings || overrides;
  const keys = [courseCopyKey(text, domain), courseCopyKey(text), text];
  const candidate = keys.map(key => (Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined)).find(value => value !== undefined);
  const choices = Array.isArray(candidate) ? candidate : [candidate];
  const selected = choices.find(value => copyOverrideMatches(value, context));
  const corrected = typeof selected === "string" ? selected : selected?.text;
  if (typeof corrected !== "string") return text;
  if (protectedSamplesChanged(text, corrected)) throw Error("English copy override changes a protected native sample or placeholder: " + context.path);
  return corrected;
}

const agrees = (expected, actual) => !expected || expected === actual;

function copyOverrideMatches(value, context) {
  if (typeof value === "string") return true;
  if (!value || typeof value !== "object") return false;
  return agrees(value.path, context.path) && agrees(value.nativeText, context.nativeText)
    && agrees(value.speaker, context.speaker) && agrees(value.learnerGender, context.gender);
}

function protectedSamplesChanged(text, corrected) {
  return JSON.stringify(nativeSamples(text)) !== JSON.stringify(nativeSamples(corrected))
    || JSON.stringify(placeholders(text)) !== JSON.stringify(placeholders(corrected));
}

/** Only mixed pronunciation explanations are copy. Pure romanisation and glyph formulas remain native. */
export function phoneticCopyFragments(text, field) {
  if (field === "reading" || !phoneticFields.has(field) || !phoneticDescription.test(text)) return null;
  const fragments = [...text.matchAll(/[\p{L}\p{M}]+/gu)].map(match => match[0]).filter(word => !englishPhoneticWords.has(word.toLowerCase()));
  // An initial a in "a, short" is the vowel, while a in "changes a consonant" is English.
  if (/^a(?:\s*[,/]\s*|\s+(?:short|long)\b)/i.test(text)) fragments.push(text[0]);
  return [...new Set(fragments)];
}

const optionalField = (present, key, value) => present ? { [key]: value } : {};

function copyContextRecord(domain, context) {
  return {
    domain,
    path: context.path,
    field: context.field,
    ...optionalField(context.gender, "learnerGender", context.gender),
    ...optionalField(context.speaker, "speaker", context.speaker),
    ...optionalField(context.speakerGender, "speakerGender", context.speakerGender),
    ...optionalField(context.nativeText, "nativeText", context.nativeText),
  };
}

function addCopyUse(current, record) {
  const uses = current.uses ||= [];
  if (!uses.some(entry => entry.path === record.path)) uses.push(record);
}

function recordCopyContext(key, domain, context) {
  if (!context.copyContexts) return;
  const record = copyContextRecord(domain, context);
  const current = context.copyContexts[key];
  if (!current) context.copyContexts[key] = record;
  else if (current.path !== record.path) addCopyUse(current, record);
}

function authoredFragments(fragments, original, domain) {
  return fragments[courseCopyKey(original, domain)]
    || fragments[courseCopyKey(original)]
    || fragments[original]
    || [];
}

function recordFragments(key, original, domain, context) {
  if (!context.protectedFragments) return;
  const own = context.phoneticFragments || [];
  const authored = authoredFragments(context.protectedFragments, original, domain);
  if (own.length || authored.length) context.protectedFragments[key] = [...new Set([...own, ...authored])];
}

function rememberCopyUse(key, original, text, domain, context) {
  recordCopyContext(key, domain, context);
  if ((domain || original !== text) && context.legacyKeys) context.legacyKeys[key] = courseCopyKey(original);
  recordFragments(key, original, domain, context);
}

export function reference(text, catalog, domain = "", context = {}) {
  if (!text || text.startsWith(COURSE_COPY_PREFIX)) return text;
  const original = text;
  text = courseCopySourceText(text, domain, context, context.englishOverrides || {});
  const key = context.resolveCopyKey ? context.resolveCopyKey(text, domain, context) : courseCopyKey(text, domain);
  catalog[key] = text;
  rememberCopyUse(key, original, text, domain, context);
  return COURSE_COPY_PREFIX + key;
}
function castSpeaker(value, trail) {
  const castAt = trail.lastIndexOf("cast");
  if (castAt === -1) return null;
  const next = trail[castAt + 1];
  if (!/^\d+$/.test(String(next || ""))) return next;
  return value.id;
}

function withSpeaker(value, trail, context) {
  const speaker = explicitSpeaker(value) || castSpeaker(value, trail) || context.speaker;
  const speakerGender = context.actorGenders?.[speaker] || context.speakerGender;
  const nativeSentence = typeof value.text === "string" && Array.isArray(value.segs) ? value.text : context.nativeSentence;
  return { ...context, speaker, speakerGender, nativeSentence };
}

function explicitSpeaker(value) {
  return (typeof value.who === "string" && value.who) || (typeof value.speaker === "string" && value.speaker) || undefined;
}

function skipsCopy(trail) {
  return trail.includes("performance") || trail.includes("introducedIn");
}

function profileFlags(trail, key) {
  const nativeProfile = trail.includes("language") && trail.includes("profile");
  const profileKeys = ["home", "job", "intro", "about", "role", "description", "summary", "likes", "dislikes", "traits", "paragraphs", "note"];
  const profileCopy = trail.includes("profile") && !nativeProfile && profileKeys.includes(key);
  return { nativeProfile, profileCopy };
}

function titleFlags(value, trail, key) {
  const nativeSetTitle = trail.length === 4 && trail[0] === "course" && trail[1] === "language" && trail[2] === "sets";
  const nativeTitle = nativeTitles.has(value.type) || value.kind === "comic" || trail.includes("scenes") || trail[0] === "scene" || nativeSetTitle;
  return { nativeTitle, uiTitle: key === "title" && !nativeTitle };
}

function stringFlags(value, key, item) {
  const phoneticFragments = typeof item === "string" ? phoneticCopyFragments(item, key) : null;
  return {
    phoneticFragments,
    descriptiveScope: key === "scope" && typeof item === "string" && !scopeTokens.has(item),
    matchGloss: key === "b" && typeof item === "string" && !value.bForms,
    castReference: key === "about" && ["story", "course-story"].includes(value.type),
    opaqueDirection: key === "direction" && ["ltr", "rtl", "auto"].includes(item),
    ambiguousGloss: typeof item === "string" && ["work", "right", "order"].includes(item.toLowerCase()),
  };
}

function isLanguageName(key, trail) {
  if (key !== "name") return false;
  if (trail.at(-1) === "script") return true;
  return trail.at(-1) === "language" && !trail.includes("cast");
}

function nameFlags(key, item, trail) {
  return {
    languageName: isLanguageName(key, trail),
    castIntro: key === "intro" && typeof item === "string" && trail.includes("cast") && !trail.includes("language"),
    tokenTip: trail.includes("segs") && ["w", "n", "g"].includes(key),
  };
}

function wordDomain(value, context) {
  const kind = value.pos || value.kind || "context";
  return `lexical:${kind}:${value.s}:${context.nativeSentence || ""}`;
}

function lexicalDomain(value, key, _item, context, ambiguousGloss) {
  if (!ambiguousGloss || !["en", "w"].includes(key)) return "";
  if (value.id) return `lexical:concept:${value.id}`;
  if (value.c || value.v) return `lexical:concept:${value.c || value.v}`;
  if (key !== "w") return "";
  return wordDomain(value, context);
}

function copyFlags(value, key, item, trail, context) {
  const strings = stringFlags(value, key, item);
  return {
    ...profileFlags(trail, key),
    ...titleFlags(value, trail, key),
    ...strings,
    ...nameFlags(key, item, trail),
    lexicalDomain: lexicalDomain(value, key, item, context, strings.ambiguousGloss),
    kind: value.kind || context.kind,
    scriptDisplay: trail[0] === "script" && (scriptCopyFields.has(key) || (key === "text" && value.lang === "en")),
  };
}

function copyDomain(key, flags, trail) {
  if (flags.languageName) return "language-name";
  if (flags.profileCopy) return `profile:${key}`;
  if (trail.includes("terms") && ["term", "forms"].includes(key)) return "grammar-term";
  return flags.lexicalDomain;
}

function nativeFormsOf(value, key) {
  if (key === "qEn") return value.q;
  if (key === "b" && !value.bForms) return value.a;
  if (key === "contextEn") return value.context;
  if (key === "en" && !value.word) return value.forms;
  return null;
}

function genderedShape(item) {
  if (typeof item === "string") return true;
  return !!item && typeof item === "object" && ("m" in item || "f" in item);
}

function genderedSide(item, gender, nativeForms, catalog, next, context, key, speaker) {
  const text = typeof item === "string" ? item : item[gender] ?? item.f ?? item.m;
  const nativeText = nativeForms[gender].text;
  const qualified = `spoken-meaning:${gender}:${speaker}:${nativeText}`;
  return [gender, reference(text, catalog, qualified, {
    ...context,
    path: next.join(".") + "." + gender,
    field: key,
    gender,
    speaker,
    nativeText,
  })];
}

function genderedCopy(value, key, item, catalog, next, context, nativeForms) {
  if (!nativeForms?.m?.text || !nativeForms?.f?.text || !genderedShape(item)) return null;
  const speaker = explicitSpeaker(value) || context.speaker || "narrator";
  return Object.fromEntries(["m", "f"].map(gender => genderedSide(item, gender, nativeForms, catalog, next, context, key, speaker)));
}

function namedCopyField(key, flags) {
  if (flags.scriptDisplay) return true;
  if (copyFields.has(key) || flags.tokenTip || flags.uiTitle || flags.profileCopy) return true;
  return flags.phoneticFragments !== null || flags.languageName || flags.castIntro || flags.matchGloss || flags.descriptiveScope;
}

function isInterfaceCopy(key, item, flags) {
  if (typeof item !== "string" || flags.opaqueDirection || flags.castReference || key === "reading") return false;
  if (phoneticFields.has(key) && flags.phoneticFragments === null) return false;
  return namedCopyField(key, flags) || (key === "stage" && !/^S\d+$/.test(item));
}

function fieldContext(context, value, next, key, flags) {
  const nativeValue = typeof value.word === "string" ? value.word : typeof value.text === "string" ? value.text : undefined;
  const native = flags.lexicalDomain || (key === "en" && nativeValue !== undefined)
    ? { nativeText: context.nativeSentence || nativeValue } : {};
  return { ...context, path: next.join("."), field: key, phoneticFragments: flags.phoneticFragments, ...native };
}

function childUiArray(key, kind, flags, trail) {
  if (!flags.nativeProfile && ["traits", "paragraphs", "notes", "learnNotes", "head", "rows"].includes(key)) return true;
  if (key === "forms" && trail.includes("terms")) return true;
  if (key === "options" && kind === "meaning") return true;
  return !!flags.profileCopy && (key === "likes" || key === "dislikes");
}

function childDomain(key, trail, domain) {
  if (domain) return domain;
  if (key === "traits" && trail.includes("cast") && !trail.includes("language")) return "profile:traits";
  return "";
}

function recurseContext(value, key, trail, context, flags, domain) {
  return {
    ...context,
    kind: flags.kind,
    uiArray: childUiArray(key, flags.kind, flags, trail),
    speaker: explicitSpeaker(value) || context.speaker,
    domain: childDomain(key, trail, domain),
  };
}

function copyField(value, key, item, catalog, trail, context) {
  const next = [...trail, key];
  const flags = copyFlags(value, key, item, trail, context);
  const domain = copyDomain(key, flags, trail);
  const gendered = genderedCopy(value, key, item, catalog, next, context, nativeFormsOf(value, key));
  if (gendered) return gendered;
  if (isInterfaceCopy(key, item, flags)) return reference(item, catalog, domain, fieldContext(context, value, next, key, flags));
  return extractCourseCopy(item, catalog, next, recurseContext(value, key, trail, context, flags, domain));
}

function copyArrayItem(item, index, catalog, trail, context) {
  if (typeof item === "string" && context.uiArray && !/^\*.*\*$/.test(item)) {
    return reference(item, catalog, context.domain || "", { ...context, path: [...trail, index].join(".") });
  }
  return extractCourseCopy(item, catalog, [...trail, String(index)], context);
}
/** Native forms, segment surfaces, phonetic readings, pronunciation and media are never interface copy. */
export function extractCourseCopy(value, catalog, trail = [], context = {}) {
  if (Array.isArray(value)) return value.map((item, index) => copyArrayItem(item, index, catalog, trail, context));
  if (!value || typeof value !== "object") return value;
  if (skipsCopy(trail)) return structuredClone(value);
  const nextContext = withSpeaker(value, trail, context);
  const out = {};
  for (const [key, item] of Object.entries(value)) out[key] = copyField(value, key, item, catalog, trail, nextContext);
  return out;
}
